import type {
  Message,
  Chat,
} from "../../../redux/reducers/ChatReducer/ChatSliceTypes";

/**
 * Get display name from user object or string
 * @param nameOrUser - The name string or User object to format
 * @returns Formatted name
 */
export const getName = (nameOrUser: string | any): string => {
  if (!nameOrUser) return "Unknown";

  // If it's an object (User object)
  if (typeof nameOrUser === "object") {
    // Try 'name' field first (your data structure)
    if (nameOrUser.name) {
      return nameOrUser.name;
    }

    // Fallback to first_name/last_name if they exist
    const firstName = nameOrUser.first_name || "";
    const lastName = nameOrUser.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || "Unknown";
  }

  // If it's a string, capitalize first letter
  if (typeof nameOrUser === "string") {
    return nameOrUser.charAt(0).toUpperCase() + nameOrUser.slice(1);
  }

  return "Unknown";
};

/**
 * Normalizes different date formats to timestamps for comparison
 * @param date - Date string (can be "Today", "Yesterday", or ISO format)
 * @returns Timestamp in milliseconds
 */
export const normalizeDate = (date: string): number => {
  if (date === "Today") {
    return new Date().setHours(23, 59, 59, 999);
  }
  if (date === "Yesterday") {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.setHours(23, 59, 59, 999);
  }
  return new Date(date).getTime();
};

/**
 * Arranges and deduplicates messages by date
 * Combines existing messages with new ones and sorts them chronologically
 * @param existingMessages - Previously loaded messages
 * @param newMessages - Newly fetched messages
 * @returns Arranged and deduplicated messages sorted by date
 */
export const arrangeMessages = (
  existingMessages: Message[],
  newMessages: Message[]
): Message[] => {
  // Combine messages
  const combinedMessages = [...existingMessages, ...newMessages];

  // Group by date
  const uniqueDates = new Map();

  combinedMessages.forEach((item: Message) => {
    if (!uniqueDates.has(item.date)) {
      uniqueDates.set(item.date, {
        ...item,
        messages: [...item.messages],
      });
    } else {
      const existingMessages = uniqueDates.get(item.date).messages;
      uniqueDates.set(item.date, {
        ...item,
        messages: [...existingMessages, ...item.messages],
      });
    }
  });

  const arrangedData = Array.from(uniqueDates.values());

  // Sort by date (oldest to newest)
  arrangedData.sort(
    (a: Message, b: Message) => normalizeDate(a.date) - normalizeDate(b.date)
  );

  // Sort messages within each date and remove duplicates
  arrangedData.forEach((item: Message) => {
    const seenIds = new Set();
    item.messages = item.messages
      .sort((m1: any, m2: any) => {
        const date1 = new Date(m1.created_at).getTime();
        const date2 = new Date(m2.created_at).getTime();
        return date1 - date2;
      })
      .filter((message: any) => {
        if (seenIds.has(message.id)) return false;
        seenIds.add(message.id);
        return true;
      });
  });

  return arrangedData;
};

/**
 * Filters chats based on filter type
 * @param chats - Array of chats to filter
 * @param filter - Filter type ("all" or "unread")
 * @returns Filtered array of chats
 */
export const filterChats = (
  chats: Chat[],
  filter: "all" | "unread"
): Chat[] => {
  if (!chats) return [];
  if (filter === "unread") {
    return chats.filter((chat) => chat.unread_count > 0);
  }
  return chats;
};

/**
 * Validates file size and type
 * @param file - File to validate
 * @returns Object with validation result and error message
 */
export const validateFile = (
  file: File
): { valid: boolean; error?: string } => {
  const maxFileSize = 10 * 1024 * 1024; // 10 MB
  const maxVideoSize = 5 * 1024 * 1024; // 5 MB

  const type = file.type;

  // Check video size
  if (type.startsWith("video/") && file.size > maxVideoSize) {
    return {
      valid: false,
      error: `${file.name} exceeds 5 MB limit for videos`,
    };
  }

  // Check general file size
  if (file.size > maxFileSize) {
    return {
      valid: false,
      error: `${file.name} exceeds 10 MB limit`,
    };
  }

  return { valid: true };
};

/**
 * Determines file type category from MIME type
 * @param mimeType - MIME type of the file
 * @returns File type category
 */
export const getFileTypeCategory = (
  mimeType: string
): "image" | "video" | "pdf" | "doc" | "other" => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType === "application/pdf") return "pdf";
  if (
    mimeType === "application/msword" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mimeType === "application/vnd.ms-word.document.macroEnabled.12"
  ) {
    return "doc";
  }
  return "other";
};

/**
 * Scrolls to bottom of a container
 * @param container - The container element or ref
 * @param delay - Optional delay in milliseconds before scrolling
 */
export const scrollToBottom = (
  container: HTMLDivElement | React.RefObject<HTMLDivElement>,
  delay: number = 0
): void => {
  const scroll = () => {
    const element =
      container instanceof HTMLDivElement ? container : container.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  };

  if (delay > 0) {
    setTimeout(scroll, delay);
  } else {
    scroll();
  }
};

/**
 * Checks if user has scrolled to top of container
 * @param container - The container element or ref
 * @returns True if scrolled to top
 */
export const isScrolledToTop = (
  container: HTMLDivElement | React.RefObject<HTMLDivElement>
): boolean => {
  const element =
    container instanceof HTMLDivElement ? container : container.current;
  if (!element) return false;
  return element.scrollTop === 0;
};

/**
 * Restores scroll position after loading more messages
 * @param container - The container element or ref
 * @param previousScrollTop - Previous scroll position
 */
export const restoreScrollPosition = (
  container: HTMLDivElement | React.RefObject<HTMLDivElement>,
  previousScrollTop: number
): void => {
  const element =
    container instanceof HTMLDivElement ? container : container.current;
  if (!element) return;

  const scrollDifference = element.scrollTop - previousScrollTop;
  if (element.scrollTop === 0) {
    element.scrollTop = element.scrollHeight - scrollDifference;
  }
};

/**
 * Formats file size to human readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Generates a unique temporary ID for optimistic UI updates
 * @returns Unique temporary ID string
 */
export const generateTempId = (): string => {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounces a function call
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
